import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';

import { connect } from 'react-redux';
import { Button, FormGroup, ControlLabel, FormControl, HelpBlock } from 'react-bootstrap';
import Fa from 'react-fontawesome';
import { Field, reduxForm, SubmissionError, formValueSelector } from 'redux-form';
import ellipsize from 'ellipsize';
import Toggle from 'react-bootstrap-toggle';
import Joi from 'joi';
import { format as formatCurrency } from '../../../../components/money-display';
import ProgressBar from '../../../../components/progress-bar';

// Import Actions
import { requestFetchCurrencySymbols } from '../../SetupActions';
import { getCurrencySymbols } from '../../SetupReducer';
import { requestUpdateOrganization } from '../../../Admin/AdminActions';

import { getUserCurrentOrganization } from '../../../Auth/AuthReducer';
import styles from './styles.css';

class CurrencySymbolSetupModal extends Component {
  constructor() {
    super();
    this.state = {
      loaded: false,
    };
    this.handleSubmit = this.handleSubmit.bind(this);
    this.renderField = this.renderField.bind(this);
    this.toggleDisplayOpportunityCurrency = this.toggleDisplayOpportunityCurrency.bind(this);
  }

  componentDidMount() {
    this.mounted = true;
    this.open();
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  open() {
    const { organization } = this.props;
    const data = {
      currency_symbol: organization.currency_symbol,
      currency_suffix: organization.currency_suffix,
      display_opportunity_base_currency: organization.display_opportunity_base_currency,
    };
    this.props.initialize(data);
    this.props.dispatch(requestFetchCurrencySymbols())
      .then(() => {
        this.setState({ loaded: true });
      });
  }

  handleSubmit(data) {
    const { organization } = this.props;
    return this.props.dispatch(requestUpdateOrganization(organization._id, data))
      .then(
        () => {
          this.close();
        },
        (errors) => {
          throw new SubmissionError(errors.data || errors);
        },
      );
  }

  toggleDisplayOpportunityCurrency() {
    const { selectedDisplayOpportunityCurrency } = this.props;
    this.props.change('display_opportunity_base_currency', !selectedDisplayOpportunityCurrency);
  }

  renderField({
    input, label, type, options, componentClass, meta: { touched, error },
  }) {
    const { submitting, selectedCurrencySymbol } = this.props;
    const validationState = touched && error ? 'error' : null;
    const childOptions = options;
    return (
      <FormGroup validationState={validationState}>
        <ControlLabel>{label}</ControlLabel>

        {componentClass === 'select' && <div className={styles.dropdown}>
          <FormControl
            {...input}
            componentClass={componentClass}
            type={type}
            disabled={submitting}
          >
            <option value="">&#160;&#160;&#160;No symbol</option>
            {childOptions.map((o) => {
              const label = ellipsize(o.labels.join(', '), 80, { ellipse: '...' });
              return (
                <option key={o.value} value={o.value}>
                  &#160;&#160;&#160;{o.value} ({label})&#160;&#160;&#160;
                </option>
              );
            })}
          </FormControl>
          <div className={`clearfix ${styles.dropdownDisplay}`}>
            <div className="pull-left">
              {(selectedCurrencySymbol || 'No symbol')}
            </div>
            <Fa name="caret-down" className="pull-right" />
          </div>
        </div>}
        {componentClass !== 'select' && <FormControl {...input} placeholder={label} type={type} disabled={submitting} />}
        <FormControl.Feedback />
        {touched && error && <HelpBlock>{error}</HelpBlock>}
      </FormGroup>
    );
  }

  renderExample() {
    const { selectedCurrencySymbol, selectedCurrencySuffix } = this.props;
    return (
      <div>
        Example:&nbsp;
        <code>
          {formatCurrency(1.5, true, 1, selectedCurrencySymbol, selectedCurrencySuffix)}
        </code>
      </div>
    );
  }

  renderOpportunityCurrencyToggle() {
    const { selectedDisplayOpportunityCurrency } = this.props;
    return (
      <FormGroup>
        <ControlLabel>Display Opportunity Base Currency:</ControlLabel>
        {' '}
        <Toggle
          on={<span>Enabled</span>}
          off={<span>Disabled</span>}
          onstyle="success"
          size="xs"
          offstyle="danger"
          active={selectedDisplayOpportunityCurrency}
          onClick={this.toggleDisplayOpportunityCurrency}
        />
        <HelpBlock>
          {selectedDisplayOpportunityCurrency && <div>Display opportunity amount with opportunity base currency</div>}
          {!selectedDisplayOpportunityCurrency && <div>Display opportunity amount with organization base currency</div>}
        </HelpBlock>
      </FormGroup>
    )
  }

  renderModal() {
    const { handleSubmit, submitting, valid, selectedCurrencySymbol } = this.props;
    return (
      <form onSubmit={handleSubmit(this.handleSubmit)}>
        <div className="panel panel-default">
          <div className="panel-heading">
            Customize Amount Symbol
          </div>
          <div className="panel-body">
            <FormGroup>
              <ControlLabel>Currency Symbol: <strong>{selectedCurrencySymbol}</strong></ControlLabel>
              <HelpBlock>(synced from your organization instance)</HelpBlock>
            </FormGroup>
            <Field name="currency_suffix" type="text" component={this.renderField} label="Currency Code" />
            {this.renderOpportunityCurrencyToggle()}
          </div>
          <div className="panel-footer">
            <div className="row">
              <div className="col-sm-6">
                <div className="text-left">{this.renderExample()}</div>
              </div>
              <div className="col-sm-6">
                <div className="pull-right">
                  <Button bsStyle="primary" disabled={submitting || !valid} type="submit">
                    {!submitting && <span><Fa name="edit" /> Update</span>}
                    {submitting && <span><Fa name="spinner" spin /> Updating</span>}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    );
  }

  render() {
    const { loaded } = this.state;
    return (
      <div>
        <Helmet title="Currency Symbol Setup" />
        <div className="container">
          {!loaded && <ProgressBar />}
          {loaded && this.renderModal()}
        </div>
      </div>
    );
  }
}

const selector = formValueSelector('currencySymbolSetupForm'); // <-- same as form name

// Retrieve data from store as props
function mapStateToProps(state) {
  return {
    organization: getUserCurrentOrganization(state),
    currencySymbols: getCurrencySymbols(state),
    selectedCurrencySymbol: selector(state, 'currency_symbol'),
    selectedCurrencySuffix: selector(state, 'currency_suffix'),
    selectedDisplayOpportunityCurrency: selector(state, 'display_opportunity_base_currency'),
  };
}
CurrencySymbolSetupModal.propTypes = {
  dispatch: PropTypes.func.isRequired,
  organization: PropTypes.object.isRequired,
  location: PropTypes.shape({}).isRequired,
  submitting: PropTypes.bool,
  handleSubmit: PropTypes.func,
  initialize: PropTypes.func,
  reset: PropTypes.func,
  valid: PropTypes.bool,
  selectedDisplayOpportunityCurrency: PropTypes.bool,
  currencySymbols: PropTypes.array.isRequired,
  selectedCurrencySymbol: PropTypes.string,
  selectedCurrencySuffix: PropTypes.string,
};
CurrencySymbolSetupModal.defaultProps = {
  selectedDisplayOpportunityCurrency: false,
};

const validate = (values) => {
  // Legacy constraints for reference:
  // const constraints = {
  //   currency_suffix: {
  //     length: { maximum: 5 },
  //   },
  // };

  const schema = Joi.object({
    currency_suffix: Joi.string().max(5).allow('', null).messages({
      'string.base': 'Currency suffix must be a string',
      'string.max': 'Currency suffix must be at most 5 characters',
    }),
  }).unknown(true);

  const { error } = schema.validate(values, { abortEarly: false });
  if (!error) return {};
  
  return error.details.reduce((acc, curr) => {
    if (!acc[curr.path[0]]) acc[curr.path[0]] = [];
    acc[curr.path[0]].push(curr.message);
    return acc;
  }, {});
};

const CurrencySymbolSetupForm = reduxForm({
  form: 'currencySymbolSetupForm',
  validate,
})(CurrencySymbolSetupModal);

export default connect(mapStateToProps)(CurrencySymbolSetupForm);
